import {
  createUserWithEmailAndPassword,
  deleteUser,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  linkWithCredential,
  type AuthCredential,
  type User
} from "firebase/auth";
import { getFunctions, httpsCallable, type Functions } from "firebase/functions";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { auth, firebaseApp, functions } from "../firebase/client";
import { functionsRegion } from "../config/env";
import { isNative, platform } from "../utils/platform";
import type { UserProfile } from "../types/domain";

let pendingSocialCredential: AuthCredential | null = null;

function normalizeUsername(value: string): string {
  return value.trim().normalize("NFKC").toLocaleLowerCase("en-US");
}

function isAccountExistsError(error: unknown): boolean {
  return String((error as { code?: string })?.code || "").includes("account-exists-with-different-credential");
}

async function finishUsernameLogin(user: User): Promise<User> {
  if (!pendingSocialCredential) return user;
  const credential = pendingSocialCredential;
  pendingSocialCredential = null;
  try {
    await linkWithCredential(user, credential);
  } catch (error) {
    pendingSocialCredential = credential;
    throw error;
  }
  return user;
}

function functionsErrorCode(error: unknown): string {
  return String((error as { code?: string })?.code || "").toLowerCase();
}

function shouldRetryUsernameFunction(error: unknown): boolean {
  const code = functionsErrorCode(error);
  return (
    code.includes("functions/not-found") ||
    code.includes("functions/unavailable") ||
    code.includes("functions/internal") ||
    code.includes("functions/unknown")
  );
}

function usernameLoginFunctions(): Functions[] {
  const regions = Array.from(new Set([functionsRegion, "europe-west1", "us-central1"]));
  return regions.map((region) => region === functionsRegion ? functions : getFunctions(firebaseApp, region));
}

export async function signInWithUsername(username: string, password: string): Promise<User> {
  const normalized = normalizeUsername(username);
  let firstRetryableError: unknown = null;

  for (const functionsInstance of usernameLoginFunctions()) {
    const callable = httpsCallable<{ username: string; password: string }, { customToken: string }>(
      functionsInstance,
      "loginWithUsername"
    );
    try {
      const result = await callable({ username: normalized, password });
      const customToken = String(result.data?.customToken || "").trim();
      if (!customToken) throw new Error("USERNAME_LOGIN_TOKEN_MISSING");
      const user = (await signInWithCustomToken(auth, customToken)).user;
      return finishUsernameLogin(user);
    } catch (error) {
      if (!shouldRetryUsernameFunction(error)) throw error;
      if (firstRetryableError === null) firstRetryableError = error;
    }
  }

  throw firstRetryableError ?? new Error("USERNAME_LOGIN_UNAVAILABLE");
}

export async function signInWithEmail(email: string, password: string): Promise<User> {
  return (await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password)).user;
}

export async function registerWithPassword(input: {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
}): Promise<User> {
  const check = httpsCallable<{ username: string }, { available: boolean }>(functions, "checkUsernameAvailability");
  const normalized = normalizeUsername(input.username);
  const availability = await check({ username: normalized });
  if (!availability.data.available) throw new Error("USERNAME_TAKEN");

  const credential = await createUserWithEmailAndPassword(auth, input.email.trim().toLowerCase(), input.password);
  try {
    const claim = httpsCallable<
      { firstName: string; lastName: string; username: string },
      { profile: UserProfile }
    >(functions, "claimUsernameAndProfile");
    await claim({
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      username: input.username.trim()
    });
    return credential.user;
  } catch (error) {
    await deleteUser(credential.user).catch(() => undefined);
    throw error;
  }
}

export async function signInGoogle(): Promise<User> {
  if (!isNative()) {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    try {
      return (await signInWithPopup(auth, provider)).user;
    } catch (error) {
      if (isAccountExistsError(error)) {
        pendingSocialCredential = GoogleAuthProvider.credentialFromError(error as never);
        throw new Error("ACCOUNT_LINK_REQUIRED");
      }
      throw error;
    }
  }

  const result = await FirebaseAuthentication.signInWithGoogle({
    skipNativeAuth: true,
    scopes: ["email", "profile"]
  });
  const idToken = result.credential?.idToken;
  const accessToken = result.credential?.accessToken;
  if (!idToken && !accessToken) throw new Error("GOOGLE_TOKEN_MISSING");
  const credential = GoogleAuthProvider.credential(idToken ?? null, accessToken ?? null);
  try {
    return (await signInWithCredential(auth, credential)).user;
  } catch (error) {
    if (isAccountExistsError(error)) {
      pendingSocialCredential = credential;
      throw new Error("ACCOUNT_LINK_REQUIRED");
    }
    throw error;
  }
}

export async function signInApple(): Promise<User> {
  if (platform() !== "ios") throw new Error("APPLE_IOS_ONLY");

  const result = await FirebaseAuthentication.signInWithApple({
    skipNativeAuth: true
  });
  const idToken = result.credential?.idToken;
  const nonce = result.credential?.nonce;
  if (!idToken) throw new Error("APPLE_TOKEN_MISSING");
  const provider = new OAuthProvider("apple.com");
  const credential = provider.credential({ idToken, rawNonce: nonce ?? undefined });
  try {
    return (await signInWithCredential(auth, credential)).user;
  } catch (error) {
    if (isAccountExistsError(error)) {
      pendingSocialCredential = credential;
      throw new Error("ACCOUNT_LINK_REQUIRED");
    }
    throw error;
  }
}

export async function sendReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email.trim().toLowerCase());
}

export async function logout(): Promise<void> {
  pendingSocialCredential = null;
  await Promise.allSettled([
    signOut(auth),
    isNative() ? FirebaseAuthentication.signOut() : Promise.resolve()
  ]);
}

export function currentUser(): User | null {
  return auth.currentUser;
}
